import React, { useEffect, useState } from "react";

//#region Charting Facilities
import { Line } from "react-chartjs-2";
import { CategoryScale, Chart as ChartJS, ChartDataset, Plugin, TooltipCallbacks, TooltipItem } from "chart.js/auto";
ChartJS.register(CategoryScale);
//#endregion Charting Facilities

import CustomDropDown from "input/CustomDropDown";
import { MotionRomTypeMap, isBackRom, isNeckRom, romMaxValue } from "utility/RomUtils";
import { renderLoader } from "utility/LoaderUtils";

import * as MD from "reports/measures/MeasureDef";
import * as MP from "reports/measures/MeasureProperties";
import * as SR from "reports/measures/SeverityResolver";

const RomsPanel = ({
    coachingPlanDataFound,
    currentWeek,
    dataMissing,
    isLoading,
    rawRomData
}) => {
    const [chartLabels, setChartLabels] = useState([]);
    const [includeWeekZero, setIncludeWeekZero] = useState<boolean>(false);
    const [isConfigured, setIsConfigured] = useState<boolean>(false);
    const [romChartData, setRomChartData] = useState([]);
    const [romData, setRomData] = useState(null);
    const [romOptions, setRomOptions] = useState<{}[]>(null);
    const [selectedRomType, setSelectedRomType] = useState<MD.MeasureType>(null);

    const customBackgroundSeverityColorizerPlugin: Plugin<"line"> = {
        id: "customBackgroundSeverityColorizerPlugin",
        beforeDraw: (chart, _args, severityLimits) => {
            const {
                ctx,
                chartArea: { left, width },
                scales: { y }
            } = chart;

            ctx.save();
            ctx.fillStyle = "rgba(0, 255, 0, 0.2)";
            ctx.fillRect(left, y.getPixelForValue(severityLimits.maxValue), width, y.getPixelForValue(severityLimits.lowMin) - y.getPixelForValue(severityLimits.maxValue));
            ctx.fillStyle = "rgba(173, 255, 47, 0.3)";
            ctx.fillRect(left, y.getPixelForValue(severityLimits.lowMin), width, y.getPixelForValue(severityLimits.mildMin) - y.getPixelForValue(severityLimits.lowMin));
            ctx.fillStyle = "rgba(255, 255, 0, 0.2)";
            ctx.fillRect(left, y.getPixelForValue(severityLimits.mildMin), width, y.getPixelForValue(severityLimits.moderateMin) - y.getPixelForValue(severityLimits.mildMin));
            ctx.fillStyle = "rgba(255, 165, 0, 0.2)";
            ctx.fillRect(left, y.getPixelForValue(severityLimits.moderateMin), width, y.getPixelForValue(severityLimits.severeMin) - y.getPixelForValue(severityLimits.moderateMin));
            ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
            ctx.fillRect(left, y.getPixelForValue(severityLimits.severeMin), width, y.getPixelForValue(severityLimits.verySevereMin) - y.getPixelForValue(severityLimits.severeMin));
            ctx.restore();
        }
    };

    const customBackgroundPlugin = {
        id: "customBackgroundPlugin",
        beforeDraw: (chart: ChartJS<"line">) => {
            // Lower Back ROMs
            if (isBackRom(selectedRomType)) {
                customBackgroundSeverityColorizerPlugin.beforeDraw(
                    chart,
                    null,
                    {
                        maxValue: 90,
                        lowMin: 80,
                        mildMin: 70,
                        moderateMin: 60,
                        severeMin: 15,
                        verySevereMin: 0,
                        minValue: 0
                    }
                );
            }
            // Neck ROMs
            else if (isNeckRom(selectedRomType)) {
                switch (selectedRomType) {
                    case MD.MeasureTypesMap.NeckExtension:
                        customBackgroundSeverityColorizerPlugin.beforeDraw(
                            chart,
                            null,
                            {
                                maxValue: 60,
                                lowMin: 58,
                                mildMin: 49,
                                moderateMin: 37,
                                severeMin: 25,
                                verySevereMin: 0,
                                minValue: 0
                            }
                        );
                        break;
                    case MD.MeasureTypesMap.NeckFlexion:
                        customBackgroundSeverityColorizerPlugin.beforeDraw(
                            chart,
                            null,
                            {
                                maxValue: 50,
                                lowMin: 48,
                                mildMin: 41,
                                moderateMin: 31,
                                severeMin: 21,
                                verySevereMin: 0,
                                minValue: 0
                            }
                        );
                        break;
                    case MD.MeasureTypesMap.NeckLeftRotation:
                    case MD.MeasureTypesMap.NeckRightRotation:
                        customBackgroundSeverityColorizerPlugin.beforeDraw(
                            chart,
                            null,
                            {
                                maxValue: 80,
                                lowMin: 77,
                                mildMin: 65,
                                moderateMin: 49,
                                severeMin: 33,
                                verySevereMin: 0,
                                minValue: 0
                            }
                        );
                        break;
                }
            }
            // Upper Extremity ROMs
            else {
                customBackgroundSeverityColorizerPlugin.beforeDraw(
                    chart,
                    null,
                    {
                        maxValue: 180,
                        lowMin: 165,
                        mildMin: 145,
                        moderateMin: 120,
                        severeMin: 30,
                        verySevereMin: 0,
                        minValue: 0
                    }
                );
            }
        }
    };

    const customLinePlotPlugin: Plugin<"line"> = {
        id: "customLinePlotPlugin",
        beforeDraw(chart: ChartJS<"line">) {
            const dataset = chart.data?.datasets[0] as ChartDataset<"line", number[]>;
            const data = dataset?.data;

            if (!data || !Array.isArray(data)) {
                return;
            }

            const ctx = chart.ctx;
            let xAxis = chart.scales["x"];
            let yAxis = chart.scales["y"];

            let valueFrom: number | null = null;
            let valueFromIndex = 0;
            let xFrom: number | null = null;
            let yFrom: number | null = null;

            ctx.save();
            ctx.strokeStyle = dataset.borderColor as string;

            data.forEach((value, index) => {
                if (value != null) {
                    let x = xAxis.getPixelForTick(index);
                    let y = yAxis.getPixelForValue(value);
                    if (valueFrom != null) {
                        ctx.lineWidth = (dataset.borderWidth as number) ?? 2;
                        if (index - valueFromIndex > 1) {
                            ctx.setLineDash([5, 5]);
                        }
                        else {
                            ctx.setLineDash([]);
                        }
                        ctx.beginPath();
                        ctx.moveTo(xFrom, yFrom);
                        ctx.lineTo(x, y);
                        ctx.stroke();
                    }
                    valueFrom = value;
                    valueFromIndex = index;
                    xFrom = x;
                    yFrom = y;
                }
            });
            ctx.restore();
        }
    };
    //#endregion Charting Facilities

    // ---=== Utilities ===--- //

    const processRawRom = (romType: MD.MeasureType, raw: number) => {
        const maxValue = romMaxValue(romType);
        const clippedRaw = Math.min(Math.max(raw, 0), maxValue);
        const normalizedValue = clippedRaw / maxValue;
        const severityResolver = MP.MeasurePropertiesMap[romType].severityResolver;
        const severityLevel = severityResolver(clippedRaw).severityLevel;
        return [normalizedValue, severityLevel];
    }

    const getOptionPill = (romType: MD.MeasureType, rawValue) => {
        const [normalizedValue, severityLevel] = processRawRom(romType, rawValue);
        const colorCategory = SR.getSeverityColor(romType, severityLevel);

        const colorLevel = colorCategory === 'severe' ? 0 :
                         colorCategory === 'warn' ? 2 : 4;

        return {
            "color": colorLevel,
            "displayPercent": true,
            "value": normalizedValue
        }
    };

    const tooltipRomLabelColors = (context: TooltipItem<"line">) => {
        const raw = context.raw as number;
        const [_normalizedValue, severityLevel] = processRawRom(selectedRomType, raw);
        const colorCategory = SR.getSeverityColor(selectedRomType, severityLevel);

        if (colorCategory === 'severe') return "rgba(255, 0, 0, 0.2)";
        if (colorCategory === 'warn') return "rgba(255, 165, 0, 0.2)";
        return "rgba(0,255,0, 0.2)";
    };

    const tooltipRomValuesLevels = (context: TooltipItem<"line">[]) => {
        const raw = context[0].raw as number;
        const [_normalizedValue, severityLevel] = processRawRom(selectedRomType, raw);
        return SR.getSeverityLabel(selectedRomType, severityLevel);
    };

    const tooltipRomsCallback: Partial<TooltipCallbacks<"line">> = {
        title: (context: TooltipItem<"line">[]) => {
            return [`Week ${context[0].label}`];
        },
        label: (context: TooltipItem<"line">) => {
            return `${context.formattedValue} °`;
        },
        labelPointStyle: function (_context: TooltipItem<"line">) {
            return {
                pointStyle: "rect" as const,
                rotation: 0
            };
        },
        labelColor: function (context: TooltipItem<"line">) {
            let color = tooltipRomLabelColors(context);
            return {
                borderColor: color,
                backgroundColor: color,
                borderWidth: 2
            };
        },
        footer: (context: TooltipItem<"line">[]) => {
            return [tooltipRomValuesLevels(context)];
        }
    };

    const configureChart = (romType: string) => {
        if (romData) {
            if (romData[romType]?.values) {
                let chartData = [];
                let weekLabels = [];

                for (let i = (includeWeekZero ? 0 : 1); i <= currentWeek; i++) {
                    weekLabels.push(i);
                    chartData.push(romData[romType].values[i] ? Math.round(romData[romType].values[i]) : null);
                }

                setRomChartData(chartData);
                setChartLabels(weekLabels);
            }
        }

        setIsConfigured(true);
    };

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            if (rawRomData) {
                let processedData = {};
                let romTypes: MD.MeasureTypes = [];

                for (const r in rawRomData) {
                    const rawData = rawRomData[r];
                    const week = rawData.plan_week;

                    if (includeWeekZero || week >= 1) {
                        const motion = rawData.motion;
                        const dateCreated = rawData.date_created_client;

                        const romType = MotionRomTypeMap[motion];
                        if (!romTypes.includes(romType)) {
                            romTypes.push(romType);
                        }

                        const value = Math.min(Math.max(rawData.max, 0), MP.MeasurePropertiesMap[romType].maxScore);

                        if (!(romType in processedData)) {
                            processedData[romType] = {
                                motion: motion,
                                values: { [week]: value },
                                first: {
                                    week: week,
                                    date: dateCreated,
                                    value: value
                                },
                                latest: {
                                    week: week,
                                    date: dateCreated,
                                    value: value
                                }
                            };
                        }
                        else {
                            const data = processedData[romType];

                            if (!(week in data.values)) {
                                data.values[week] = value;
                            }
                            else if (value > data.values[week]) {
                                data.values[week] = value;
                            }

                            if (week < data.first.week || (week === data.first.week && value > data.first.value)) { // && moment(dateCreated).isBefore(moment(data.first.date))) {
                                data.first = {
                                    week: week,
                                    date: dateCreated,
                                    value: value
                                };
                            }

                            if (week > data.latest.week || (week === data.latest.week && value > data.latest.value)) { // moment(dateCreated).isAfter(moment(data.latest.date))) {
                                data.latest = {
                                    week: week,
                                    date: dateCreated,
                                    value: value
                                };
                            }
                        }
                    }
                }

                let options = [];
                romTypes.sort((a, b) => {
                    const motionA = processedData[a]?.motion;
                    const motionB = processedData[b]?.motion;

                    if (motionA < motionB) return -1;
                    if (motionA > motionB) return 1;
                    return 0;
                });
                romTypes.forEach(
                    (romType: MD.MeasureType) => {
                        options.push(
                            {
                                label: processedData[romType].motion.replace(/([a-z])([A-Z])/g, "$1 $2"),
                                value: romType,
                                pill: getOptionPill(romType, processedData[romType].latest.value)
                            }
                        );
                    }
                );

                setRomData(processedData);
                setRomOptions(options);

                if (selectedRomType === null || !romTypes.includes(selectedRomType)) {
                    setSelectedRomType(romTypes[0]);
                }
            }
        }, [rawRomData, includeWeekZero]
    );

    useEffect(
        () => {
            if (selectedRomType) {
                configureChart(selectedRomType);
            }
        }, [romData, selectedRomType, includeWeekZero]
    );

    // ---=== Render ===--- //

    const renderTable = () => {
        let table = [];
        const data = romData[selectedRomType];
        const maxValue = MP.MeasurePropertiesMap[selectedRomType].maxScore;
        const first = Math.round(data.first.value);
        const latest = Math.round(data.latest.value);
        const totalChange = Math.round((latest/maxValue - first/maxValue) * 100);
        const weeks = Object.keys(data.values);
        const recent = Math.round(data.values[weeks[weeks.length > 1 ? weeks.length - 2 : 0]]);
        const last = Math.round(data.values[weeks[weeks.length - 1]]);
        const recentChange = Math.round((last/maxValue - recent/maxValue) * 100);

        table.push(
            <React.Fragment key={`rom-panel-chart-footer-row-1`}>
                <tr className="border-y-0 even:bg-white odd:bg-white border-gray-200">
                    <td className="w-1/2 border-x-0 border-t-0 border-b-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">First Measurement</span>
                        <div className="float-end">{`${first}°`}</div>
                    </td>
                    <td className="w-1/2 border-x-0 border-t-0 border-b-0  px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">All-Time Change</span>
                        <div className="align-text-bottom has-tooltip float-bottom text-center content-center items-center pl-2 object-bottom whitespace-nowrap inline-flex">
                            <span className="tooltip rounded shadow-lg p-1 mb-14">
                                Change between first
                                <br/>
                                and latest measurements
                            </span>
                            <img className="h-4 w-4 hs-tooltip" src="/icons/help-circle.svg" alt="icon"/>
                        </div>
                        <div className={`float-end ${totalChange >= 0 ? "text-success-600" : "text-error-600"}`}>
                            {`${totalChange >= 0 ? "+" : ""}${totalChange}%`}
                        </div>
                    </td>
                </tr>
            </React.Fragment>
        );
        table.push(
            <React.Fragment key={`rom-panel-chart-footer-row-2`}>
                <tr className="border-y-2 even:bg-white odd:bg-white border-gray-200">
                    <td className="w-1/2 border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">Latest Measurement</span>
                        <div className="float-end">
                            {`${latest}°`}
                        </div>
                    </td>
                    <td className="w-1/2 grow shrink border-t-0 border-b-0 border-x-0 px-6 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <p className="whitespace-nowrap inline-flex">
                            <span className="text-gray-900">Recent Change</span>
                        </p>
                        <div className="align-text-bottom has-tooltip float-bottom text-center content-center items-center pl-2 object-bottom whitespace-nowrap inline-flex">
                            <span className="tooltip rounded shadow-lg p-1 ml-4 mb-12">
                                Change from previous
                                <br/>
                                weekly best
                            </span>
                            <img className="h-4 w-4 hs-tooltip" src="/icons/help-circle.svg" alt="icon"/>
                        </div>
                        <div className={`float-end ${recentChange >= 0 ? "text-success-600" : "text-error-600"}`}>
                            {`${recentChange >= 0 ? "+" : ""}${recentChange}%`}
                        </div>
                    </td>
                </tr>
            </React.Fragment>
        );

        return table;
    };

    return (
        <React.Fragment>
            {
                (isLoading || !isConfigured) && !dataMissing
                    ? (
                        renderLoader("Loading ROM Data", 50)
                    )
                    : (
                        dataMissing
                            ? (
                                <div className="w-full h-full justify-center gap-6 items-center inline-flex flex-col">
                                    <div className="w-14 h-14 p-3.5 bg-[#ffe9e2] rounded-full border border-gray-50 justify-center items-center inline-flex">
                                        <img className="w-7 h-7 relative flex-col justify-start items-start flex" src="/icons/data.svg" alt="icon"/>
                                    </div>
                                    <div className="text-wrap w-[80%] break-words">
                                        <div className="self-stretch text-center text-gray-700 text-md font-semibold leading-[18px]">No ROM Data Available</div>
                                    </div>
                                    <div className="break-words inline-block text-center text-gray-500 text-base font-normal">
                                        {
                                            coachingPlanDataFound
                                                ? (
                                                    <>
                                                        Coaching plan active. No ROMs recorded yet.
                                                    </>
                                                )
                                                : (
                                                    <>
                                                    No active coaching plan found.
                                                    </>
                                                )
                                        }
                                    </div>
                                </div>
                            )
                            : (
                                <div className=" w-full h-[90%] justify-start items-center inline-flex flex-col">
                                    <div className="w-full h-[10%] gap-5 inline-flex justify-start items-center">
                                        <div className="grow shrink basis-0 ml-[26px] flex items-center flex-wrap">
                                            <span className="text-xl font-semibold text-gray-900 whitespace-nowrap">Range of Motion History</span>
                                            <div className="flex items-center gap-2 ml-4">
                                                <input
                                                    className="w-4 h-4 p-0.5 rounded border border-gray-300 cursor-pointer"
                                                    type="checkbox"
                                                    checked={includeWeekZero}
                                                    onChange={() => setIncludeWeekZero(prev => !prev)}
                                                />
                                                <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Include Week 0</span>
                                            </div>
                                        </div>
                                        <span className="gap-0 mr-[26px]">
                                            <div className="mr-2 align-text-bottom has-tooltip float-bottom text-center content-center items-center pl-2 object-bottom whitespace-nowrap inline-flex">
                                                <span className="tooltip rounded shadow-lg p-1 -ml-56 mt-16 align-text-bottom">
                                                    Select motion to view.
                                                    <br/>
                                                    Colored decorator shows latest measurement
                                                    <br/>
                                                    as percentage of full range.
                                                </span>
                                                <img className="h-[18px] w-[18px] hs-tooltip" src="/icons/help-circle.svg" alt="icon"/>
                                            </div>
                                            <div className="flex-col justify-start items-start inline-flex mr-2">
                                                <CustomDropDown
                                                    defaultValue={selectedRomType}
                                                    optionPillBackgroundColors={["bg-[#ff000033]", "bg-[#ffa50033]", "bg-[#ffff0033]", "bg-[#adff2f4d]", "bg-[#00ff0033]"]}
                                                    optionPillTextColors={["text-[#D32F2F]", "text-[#FFA000]", "text-[#FBC02D]", "text-[#689F38]", "text-[#388E3C]"]}
                                                    options={romOptions}
                                                    // Callbacks
                                                    onSelectionCallback={setSelectedRomType}
                                                />
                                            </div>
                                        </span>
                                    </div>
                                    <div className="w-[calc(100%-48px)] h-[65%] mt-5">
                                        <Line
                                            key={`chart-${selectedRomType}`}
                                            data={
                                                {
                                                    labels: chartLabels,
                                                    datasets: [
                                                        {
                                                            label: "Rom",
                                                            data: romChartData,
                                                            fill: false,
                                                            borderColor: "#2196f3",
                                                            backgroundColor: "#2196f3",
                                                            borderWidth: 1,
                                                            spanGaps: true,
                                                            segment: {
                                                                borderDash: ctx => {
                                                                    return !romChartData[ctx.p0DataIndex] || !romChartData[ctx.p1DataIndex] ? [10, 19] : [];
                                                                },
                                                                borderColor: ctx => {
                                                                    return !romChartData[ctx.p0DataIndex] || !romChartData[ctx.p1DataIndex] ? "#808080" : "#2196f3";
                                                                }
                                                            }
                                                        }
                                                    ]
                                                }
                                            }
                                            options={
                                                {
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    clip: false,
                                                    plugins: {
                                                        tooltip: {
                                                            usePointStyle: true,
                                                            footerFont: { weight: "normal" },
                                                            callbacks: tooltipRomsCallback
                                                        },
                                                        legend: {
                                                            display: false
                                                        }
                                                    },
                                                    scales: {
                                                        x: {
                                                            display: true,
                                                            grid: {
                                                                display: false
                                                            },
                                                            title: {
                                                                display: true,
                                                                text: "Week",
                                                                font: {
                                                                    family: "Inter"
                                                                }
                                                            }
                                                        },
                                                        y: {
                                                            beginAtZero: true,
                                                            max: romMaxValue(selectedRomType),
                                                            ticks: {
                                                                stepSize: 30,
                                                                callback: function (value, _index, _ticks) {
                                                                    return `${value} °`;
                                                                }
                                                            },
                                                            display: true,
                                                            title: {
                                                                display: true,
                                                                text: "Best Measurement",
                                                                font: {
                                                                    family: "Inter"
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                            plugins={
                                                [
                                                    customBackgroundPlugin,
                                                    customLinePlotPlugin
                                                ]
                                            }
                                        />
                                    </div>
                                    <div className="w-[calc(100%-48px)] border-0">
                                        <table className="w-full h-[100%] divide-gray-700 border-0 dark:divide-gray-700 rounded-[10px]">
                                            <tbody className="border-0">
                                                <React.Fragment>
                                                    {renderTable()}
                                                </React.Fragment>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )
                    )
            }
        </React.Fragment>
    );
};

export default RomsPanel;
